import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Chip,
  Box,
  Stack,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import moment from "moment";
import LoadingButton from "@mui/lab/LoadingButton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import ClientSubscriptionTransactions from "../ClientPortal/ClientSubscriptionTransactions";

// User-products API returns array or { items: [...] }; normalize to array with priceId set
const toUserProductsList = (response) => {
  const list = Array.isArray(response)
    ? response
    : response?.items && Array.isArray(response.items)
      ? response.items
      : [];
  return list.map((p) => ({
    ...p,
    priceId: p.priceId ?? p.price_id ?? "",
  }));
};

// Build a table row from a user-product item (purchased or assigned; API has productName, priceDescription, price, etc.)
const userProductToRow = (item, isPurchased) => ({
  product: {
    name: item.productName ?? "Product",
    productId: item.productId ?? item.product_id,
  },
  price: {
    priceId: item.priceId,
    unitAmount: item.unitAmount ?? item.unit_amount ?? item.price ?? 0,
    amountInCents: false,
    metadata: {
      description: item.priceDescription ?? item.description ?? "",
    },
    recurring: item.recurring,
  },
  purchasedProduct: isPurchased ? item : undefined,
  assignedProduct: !isPurchased ? item : undefined,
  isPurchased,
  isAssigned: !isPurchased,
});

const catalogProductsToRows = (products = []) => {
  const rows = [];
  for (const product of products || []) {
    const prices = Array.isArray(product?.prices) ? product.prices : [];
    for (const price of prices) {
      const priceId = price?.priceId ?? price?.price_id ?? "";
      if (!priceId) continue;
      rows.push({
        product: {
          name: product?.name ?? "Product",
          productId: product?.productId ?? product?.id ?? "",
        },
        price: {
          priceId,
          unitAmount:
            price?.unitAmount ?? price?.unit_amount ?? price?.price ?? 0,
          amountInCents: true,
          metadata: {
            description: price?.metadata?.description ?? "",
          },
          recurring: price?.recurring,
        },
        isPurchased: false,
        isAssigned: false,
      });
    }
  }
  return rows;
};

const formatCurrency = (amount, amountInCents) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  if (amountInCents) {
    return `$${(value / 100).toFixed(2)}`;
  }
  return `$${value.toFixed(2)}`;
};

const ManageProductsDialog = ({
  open,
  onClose,
  onSaved,
  client,
  initialProducts,
  initialPurchasedProducts,
}) => {
  const {
    getUserProducts,
    getProducts,
    postUserProducts,
    patchUserProduct,
    cancelSubscription,
    uncancelSubscription,
  } = useApi();
  const { stripeAccountId } = useUserInfo();
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [clientPurchasedProducts, setClientPurchasedProducts] = useState([]);
  const [quantityValues, setQuantityValues] = useState({});
  const [subscriptionActionLoading, setSubscriptionActionLoading] =
    useState(null);
  const [extendBillingDialog, setExtendBillingDialog] = useState(null);
  const [extendBillingDate, setExtendBillingDate] = useState(null);
  const [extendBillingSaving, setExtendBillingSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("purchased");
  const [confirmState, setConfirmState] = useState(null);

  const loadProductsData = async () => {
    if (!client) return;
    setProductsLoading(true);
    try {
      const [purchasedRes, assignedRes, catalogRes] = await Promise.all([
        getUserProducts(client.id, "purchased"),
        getUserProducts(client.id, "assigned"),
        getProducts(stripeAccountId),
      ]);
      const clientPurchasedProducts = toUserProductsList(purchasedRes);
      const clientProducts = toUserProductsList(assignedRes);
      const catalog = Array.isArray(catalogRes) ? catalogRes : [];

      setAssignedProducts(clientProducts);
      setClientPurchasedProducts(clientPurchasedProducts);
      setCatalogProducts(catalog);
      const purchasedPriceIds = new Set(
        clientPurchasedProducts.map((p) => p.priceId).filter(Boolean)
      );
      const assignedPriceIds = clientProducts
        .filter((p) => !purchasedPriceIds.has(p.priceId))
        .map((p) => p.priceId);
      setSelectedProducts(assignedPriceIds);
    } catch (error) {
      console.error("Failed to load products:", error);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setExtendBillingDialog(null);
      setExtendBillingDate(null);
      setExtendBillingSaving(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && client) {
      setActiveTab("purchased");
      setQuantityValues({});
      loadProductsData();
    }
  }, [open, client?.id]);

  useEffect(() => {
    if (Array.isArray(initialProducts) && initialProducts.length > 0) {
      setCatalogProducts(initialProducts);
    }
  }, [initialProducts]);

  const handleSave = async () => {
    try {
      if (Object.keys(quantityValues).length > 0) {
        await Promise.all(
          Object.entries(quantityValues).map(([priceId, value]) =>
            handleQuantityChange(priceId, value)
          )
        );
      }
      if (activeTab === "assigned") {
        const items = selectedProducts
          .map((priceId) => {
            const row = productRows.find((r) => r.price?.priceId === priceId);
            const productId =
              row?.product?.productId ||
              assignedProducts.find((p) => p.priceId === priceId)?.productId;
            return productId ? { productId, priceId } : null;
          })
          .filter(Boolean);
        if (items.length) {
          await postUserProducts({
            items,
            type: "assigned",
            userId: client.id,
          });
        }
      }
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Failed to save:", error);
      alert("Failed to save. Please try again.");
    }
  };

  const handleQuantityChange = async (priceId, newValue) => {
    try {
      await patchUserProduct({
        userId: client.id,
        priceId,
        action: "updateQuantity",
        value: parseInt(newValue),
      });

      setClientPurchasedProducts((prev) =>
        prev.map((product) =>
          product.priceId === priceId
            ? { ...product, quantityLeft: parseInt(newValue) }
            : product
        )
      );

      setQuantityValues((prev) => ({
        ...prev,
        [priceId]: newValue,
      }));
    } catch (error) {
      console.error("Failed to update quantity:", error);
      alert("Failed to update quantity. Please try again.");
    }
  };

  const productRows = useMemo(() => {
    const catalogRows = catalogProductsToRows(catalogProducts || []);
    const purchasedRows = (clientPurchasedProducts || []).map((item) =>
      userProductToRow(item, true)
    );
    const assignedRows = (assignedProducts || []).map((item) =>
      userProductToRow(item, false)
    );
    const byPriceId = new Map();

    for (const row of catalogRows) {
      byPriceId.set(row.price.priceId, row);
    }
    for (const row of assignedRows) {
      byPriceId.set(row.price.priceId, {
        ...(byPriceId.get(row.price.priceId) || {}),
        ...row,
        isAssigned: true,
      });
    }
    for (const row of purchasedRows) {
      byPriceId.set(row.price.priceId, {
        ...(byPriceId.get(row.price.priceId) || {}),
        ...row,
        isPurchased: true,
      });
    }

    return Array.from(byPriceId.values());
  }, [catalogProducts, clientPurchasedProducts, assignedProducts]);

  const visibleRows = useMemo(() => {
    if (activeTab === "purchased") {
      const purchased = productRows.filter((row) => row.isPurchased);
      const hasAnyWithQuantity = purchased.some(
        (row) => (row.purchasedProduct?.quantityLeft ?? 0) > 0
      );
      if (hasAnyWithQuantity) {
        return purchased.filter(
          (row) => (row.purchasedProduct?.quantityLeft ?? 0) > 0
        );
      }
      return purchased;
    }
    // Assigned tab should show all catalog products that can be assigned.
    // Keep already-purchased products visible for context, but those checkboxes stay disabled.
    return productRows;
  }, [productRows, activeTab]);

  const canShowCancelButton = (price, purchasedProduct) =>
    (!!price.recurring || !!purchasedProduct?.recurring) &&
    !!purchasedProduct?.subscriptionId;

  const handleCancelSubscription = async (subscriptionId, priceId) => {
    if (!subscriptionId) return;
    setSubscriptionActionLoading(priceId);
    setConfirmState(null);
    try {
      const updated = await cancelSubscription(subscriptionId, stripeAccountId);
      await patchUserProduct({
        userId: client.id,
        priceId,
        action: "cancel",
      });
      setClientPurchasedProducts((prev) =>
        prev.map((p) =>
          p.priceId === priceId
            ? {
                ...p,
                cancel_at_period_end: updated?.cancel_at_period_end,
                status: "cancelled",
              }
            : p
        )
      );
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      alert("Failed to cancel subscription. Please try again.");
    }
    setSubscriptionActionLoading(null);
  };

  const canShowExtendBillingButton = (price, purchasedProduct) =>
    !!price.recurring &&
    !!purchasedProduct?.subscriptionId &&
    !purchasedProduct?.cancel_at_period_end &&
    purchasedProduct?.status !== "cancelled";

  const openExtendBillingDialog = (priceId, purchasedProduct) => {
    const nextIso =
      purchasedProduct?.nextBillingDate ||
      purchasedProduct?.billingDate ||
      purchasedProduct?.startDate;
    const nextMoment =
      nextIso && moment(nextIso).isValid() ? moment(nextIso).startOf("day") : null;
    const minMoment =
      nextMoment && nextMoment.isAfter(moment())
        ? nextMoment.clone().add(1, "day")
        : moment().add(1, "day").startOf("day");
    let defaultMoment = minMoment.clone().add(7, "day");
    if (defaultMoment.isSameOrBefore(minMoment)) {
      defaultMoment = minMoment.clone().add(1, "day");
    }
    setExtendBillingDate(defaultMoment);
    setExtendBillingDialog({
      priceId,
      minMoment,
    });
  };

  const handleExtendBillingConfirm = async () => {
    if (!extendBillingDialog?.priceId || !extendBillingDate?.isValid()) return;
    setExtendBillingSaving(true);
    try {
      await patchUserProduct({
        userId: client.id,
        priceId: extendBillingDialog.priceId,
        action: "updateBillingDate",
        value: extendBillingDate.clone().endOf("day").toISOString(),
      });
      await loadProductsData();
      setExtendBillingDialog(null);
      setExtendBillingDate(null);
    } catch (error) {
      console.error("Failed to extend billing date:", error);
      alert(
        "Could not extend the billing date. The subscription may still be renewing, or Stripe rejected the date."
      );
    } finally {
      setExtendBillingSaving(false);
    }
  };

  const handleUncancelSubscription = async (subscriptionId, priceId) => {
    if (!subscriptionId) return;
    setSubscriptionActionLoading(priceId);
    setConfirmState(null);
    try {
      const updated = await uncancelSubscription(
        subscriptionId,
        stripeAccountId
      );
      setClientPurchasedProducts((prev) =>
        prev.map((p) =>
          p.priceId === priceId
            ? { ...p, cancel_at_period_end: updated?.cancel_at_period_end }
            : p
        )
      );
    } catch (error) {
      console.error("Failed to reinstate subscription:", error);
      alert("Failed to reinstate subscription. Please try again.");
    }
    setSubscriptionActionLoading(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Manage Products for {client?.name}</DialogTitle>
        <DialogContent>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{ mb: 2 }}
          >
            <Tab
              value="purchased"
              label={`Purchased (${
                productRows.filter((r) => r.isPurchased).length
              })`}
            />
            <Tab
              value="assigned"
              label={`Assigned (${
                productRows.filter((r) => r.isAssigned).length
              })`}
            />
            <Tab value="transactions" label="Transactions" />
          </Tabs>

          {activeTab === "transactions" ? (
            <ClientSubscriptionTransactions
              profileId={client?.id}
              title="Payment history"
              paperSx={{
                mt: 0,
                p: 0,
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            />
          ) : productsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : visibleRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {activeTab === "purchased"
                ? "No purchased products found for this client."
                : "No assignable products available."}
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    {activeTab === "purchased" ? (
                      <>
                        <TableCell align="center">Billing Date</TableCell>
                        <TableCell align="center">Cancel</TableCell>
                      </>
                    ) : (
                      <TableCell align="center">Assign</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map(
                    ({
                      price,
                      product,
                      purchasedProduct,
                      assignedProduct,
                      isPurchased,
                      isAssigned,
                    }) => (
                      <TableRow key={price.priceId}>
                        <TableCell>{product.name ?? "—"}</TableCell>
                        <TableCell>
                          {price.metadata?.description ?? "—"}
                        </TableCell>
                        <TableCell align="right">
                          {price.unitAmount != null
                            ? formatCurrency(
                                price.unitAmount,
                                price.amountInCents
                              )
                            : "—"}
                        </TableCell>
                        <TableCell align="center">
                          {isPurchased ? (
                            purchasedProduct?.status === "cancelled" ||
                            purchasedProduct?.cancel_at_period_end ? (
                              <Chip
                                label={
                                  purchasedProduct?.cancel_at_period_end
                                    ? "Cancelling"
                                    : "Cancelled"
                                }
                                color="default"
                                size="small"
                              />
                            ) : (
                              (() => {
                                const purchaseDateRaw =
                                  purchasedProduct?.startDate ||
                                  purchasedProduct?.createdAt ||
                                  purchasedProduct?.created_at ||
                                  purchasedProduct?.purchaseDate ||
                                  purchasedProduct?.purchasedAt;
                                const purchaseDateLabel =
                                  purchaseDateRaw && moment(purchaseDateRaw).isValid()
                                    ? moment(purchaseDateRaw).format("MMM D, YYYY")
                                    : null;
                                return (
                              <Chip
                                label={
                                  purchaseDateLabel
                                    ? `Purchased • ${purchaseDateLabel}`
                                    : "Purchased"
                                }
                                color="success"
                                size="small"
                              />
                                );
                              })()
                            )
                          ) : isAssigned ? (
                            <Chip
                              label="Assigned"
                              color="primary"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="Not Active"
                              color="default"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {price.recurring ? (
                            "n/a"
                          ) : isPurchased ? (
                            <TextField
                              type="number"
                              size="small"
                              value={
                                quantityValues[price.priceId] ??
                                purchasedProduct?.quantityLeft ??
                                0
                              }
                              onChange={(e) =>
                                setQuantityValues((prev) => ({
                                  ...prev,
                                  [price.priceId]: e.target.value,
                                }))
                              }
                              inputProps={{ min: 0 }}
                              sx={{ width: "80px" }}
                            />
                          ) : isAssigned ? (
                            assignedProduct?.quantityLeft ?? 0
                          ) : null}
                        </TableCell>

                        {activeTab === "purchased" ? (
                          <>
                            <TableCell align="center">
                              <Stack spacing={0.75} alignItems="center">
                                {(() => {
                                  const billingOrStart =
                                    purchasedProduct?.nextBillingDate ||
                                    purchasedProduct?.billingDate ||
                                    purchasedProduct?.startDate;
                                  const dateValid =
                                    billingOrStart &&
                                    moment(billingOrStart).isValid();
                                  const formatted =
                                    dateValid &&
                                    moment(billingOrStart).format(
                                      "MMM D, YYYY",
                                    );

                                  let body = null;
                                  if (price.recurring && formatted) {
                                    body = (
                                      <Typography
                                        variant="body2"
                                        whiteSpace="nowrap"
                                      >
                                        {formatted}
                                      </Typography>
                                    );
                                  } else if (billingOrStart && formatted) {
                                    body = (
                                      <Typography
                                        variant="body2"
                                        whiteSpace="nowrap"
                                      >
                                        {formatted}
                                      </Typography>
                                    );
                                  } else {
                                    body = (
                                      <Typography
                                        color="text.secondary"
                                        variant="body2"
                                      >
                                        {price.recurring ? "—" : "n/a"}
                                      </Typography>
                                    );
                                  }

                                  return (
                                    <>
                                      {body}
                                      {canShowExtendBillingButton(
                                        price,
                                        purchasedProduct,
                                      ) ? (
                                        <Button
                                          size="small"
                                          variant="text"
                                          color="primary"
                                          onClick={() =>
                                            openExtendBillingDialog(
                                              price.priceId,
                                              purchasedProduct,
                                            )
                                          }
                                        >
                                          Extend billing
                                        </Button>
                                      ) : null}
                                    </>
                                  );
                                })()}
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              {canShowCancelButton(price, purchasedProduct) ? (
                                purchasedProduct.cancel_at_period_end ? (
                                  <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() =>
                                      setConfirmState({
                                        type: "reinstate",
                                        subscriptionId:
                                          purchasedProduct.subscriptionId,
                                        priceId: price.priceId,
                                      })
                                    }
                                    loading={
                                      subscriptionActionLoading ===
                                      price.priceId
                                    }
                                  >
                                    Reinstate
                                  </LoadingButton>
                                ) : (
                                  <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() =>
                                      setConfirmState({
                                        type: "cancel",
                                        subscriptionId:
                                          purchasedProduct.subscriptionId,
                                        priceId: price.priceId,
                                      })
                                    }
                                    loading={
                                      subscriptionActionLoading ===
                                      price.priceId
                                    }
                                  >
                                    Cancel
                                  </LoadingButton>
                                )
                              ) : (
                                <Typography
                                  color="text.secondary"
                                  variant="body2"
                                >
                                  n/a
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        ) : (
                          <TableCell align="center">
                            <Checkbox
                              checked={selectedProducts.includes(price.priceId)}
                              onChange={() => {
                                setSelectedProducts((prev) =>
                                  prev.includes(price.priceId)
                                    ? prev.filter((id) => id !== price.priceId)
                                    : [...prev, price.priceId]
                                );
                              }}
                              disabled={isPurchased}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!confirmState}
        onClose={() => setConfirmState(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmState?.type === "cancel" && "Cancel Subscription"}
          {confirmState?.type === "reinstate" && "Reinstate Subscription"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmState?.type === "cancel" &&
              "Cancel this subscription at the end of the billing period? The client will retain access until then."}
            {confirmState?.type === "reinstate" &&
              "Reinstate this subscription? It will continue to renew."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmState(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (confirmState?.type === "cancel") {
                handleCancelSubscription(
                  confirmState.subscriptionId,
                  confirmState.priceId
                );
              } else if (confirmState?.type === "reinstate") {
                handleUncancelSubscription(
                  confirmState.subscriptionId,
                  confirmState.priceId
                );
              }
            }}
            variant="contained"
            color="primary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!extendBillingDialog}
        onClose={() =>
          extendBillingSaving ? undefined : setExtendBillingDialog(null)
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Extend next billing date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pushes Stripe&apos;s next charge for this subscription to the date
            you pick (coach-only; same as Stripe trial/end behavior on the
            backend).
          </Typography>
          <DatePicker
            label="Next billing date"
            value={extendBillingDate}
            onChange={(v) => setExtendBillingDate(v)}
            minDate={extendBillingDialog?.minMoment ?? undefined}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                margin: "dense",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              extendBillingSaving ? undefined : setExtendBillingDialog(null)
            }
            disabled={extendBillingSaving}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={extendBillingSaving}
            onClick={handleExtendBillingConfirm}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageProductsDialog;
